<script setup lang="ts">
import { EditPencil, NavArrowLeft, Trash } from "@iconoir/vue";
import { useCrud } from "~/composables/use-crud";
import type { Workspace } from "@artech-global/types";
import WorkspaceFormModal from "./WorkspaceFormModal.vue";
import MemberList from "./MemberList.vue";
import SubscriptionCard from "./SubscriptionCard.vue";
import WorkspaceOrderList from "~/views/workspace-order/WorkspaceOrderList.vue";
import ManualSaleModal from "~/views/workspace-order/ManualSaleModal.vue";
import OrderList from "~/views/order/OrderList.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "WorkspaceDetail" });

const route = useRoute();
const { t } = useI18n();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const workspaceID = computed(() => String(route.params.id || ""));

const {
  formModel,
  loadLoading,
  errorMsg,
  load,
  del,
} = useCrud<Workspace>({
  endpoint: "/workspaces",
  defaultFormModel: () =>
    ({
      id: "",
      name: "",
      code: "",
      desc: "",
      image: "",
    }) as unknown as Workspace,
});

const formRef = ref<InstanceType<typeof WorkspaceFormModal> | null>(null);
const saleRef = ref<InstanceType<typeof ManualSaleModal> | null>(null);

const workspace = computed(() => formModel as unknown as Workspace);
const tabs = computed(() =>
  [
    { value: "overview", label: t("workspace.tabOverview") },
    { value: "users", label: t("workspace.tabUsers"), perm: AdminPermission.UsersRead },
    { value: "orders", label: t("workspace.tabOrders"), perm: AdminPermission.OrdersRead },
    { value: "workspace-orders", label: t("workspace.tabWorkspaceOrders"), perm: AdminPermission.WorkspaceOrdersRead },
  ].filter((tab) => !tab.perm || can(tab.perm)),
);
const activeTab = computed({
  get: () => {
    const tab = String(route.query.tab || "overview");
    return tabs.value.some((tb) => tb.value === tab) ? tab : "overview";
  },
  set: (tab: string) => {
    navigateTo({ query: { ...route.query, tab } });
  },
});

function openEdit() {
  formRef.value?.open(workspaceID.value);
}

function openChangePlan() {
  // "change" only moves the plan on a live subscription; a free/trialing or
  // missing one needs a "new" sale so the billing period gets set.
  const status = workspace.value.subscription?.status;
  const type = status === "active" || status === "past-due" ? "change" : "new";
  saleRef.value?.open(undefined, { workspaceID: workspaceID.value, type });
}

async function confirmDelete() {
  const ok = await alert.negative({
    title: t("workspace.deleteConfirmTitle"),
    description: t("workspace.deleteConfirmBody", {
      name: workspace.value.name || "",
    }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(workspaceID.value);
    toast.success({ title: t("message.deleteSuccess") });
    await navigateTo("/workspace");
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

async function reload() {
  try {
    await load(workspaceID.value);
    if (workspace.value.name) {
      setTitle(workspace.value.name);
    }
  } catch (e: any) {
    toast.error({
      title: e?.data?.message || errorMsg.value || t("workspace.loadError"),
    });
  }
}

async function onSaved() {
  await reload();
}

onMounted(async () => {
  await reload();
});

watch(workspaceID, async (val) => {
  if (val) await reload();
});
</script>

<template>
  <div class="flex flex-col gap-medium padding-bottom-medium md:padding-bottom-large">
    <div class="flex items-center gap-small">
      <Button
        variant="text-neutral"
        size="small"
        @click="navigateTo('/workspace')"
      >
        <NavArrowLeft class="icon" />
        {{ t("workspace.backToList") }}
      </Button>
    </div>


    <Tabs v-if="workspace.id" v-model="activeTab" :items="tabs">
      <template #overview>
        <div class="flex flex-col gap-medium margin-top-medium">
          <Card class="flex flex-col gap-medium">
            <div class="flex flex-col gap-medium md:flex-row md:items-start">
              <Avatar
                :src="workspace.image"
                :initials="(workspace.name || '').substring(0, 2).toUpperCase()"
                size="large"
                shape="square"
              />
              <div class="flex flex-col flex-1 min-w-0 gap-xsmall">
                <div class="flex items-center gap-small flex-wrap">
                  <h1 class="h3-primary">{{ workspace.name }}</h1>
                  <Badge v-if="(workspace as any).code" intent="neutral" emphasis="medium">
                    {{ (workspace as any).code }}
                  </Badge>
                </div>
                <p v-if="workspace.desc" class="text-body-tertiary content-neutral-medium">
                  {{ workspace.desc }}
                </p>
              </div>
              <div class="flex items-center gap-xsmall">
              <ButtonIcon
                v-if="can(AdminPermission.WorkspacesWrite)"
                variant="neutral-flat"
                size="small"
                :title="t('action.edit')"
                :aria-label="t('action.edit')"
                @click="openEdit"
              >
                <EditPencil class="icon" />
              </ButtonIcon>
                <ButtonIcon
                  v-if="can(AdminPermission.WorkspacesDelete)"
                  variant="neutral-flat"
                  size="small"
                  :title="t('action.delete')"
                  :aria-label="t('action.delete')"
                  @click="confirmDelete"
                >
                  <Trash class="icon" />
                </ButtonIcon>
              </div>
            </div>

            <div class="flex flex-col gap-small">
              <div class="flex flex-col gap-3xsmall md:flex-row md:gap-small">
                <span class="flex-1 text-body-secondary content-neutral-medium">{{ t("workspace.defaultLocaleLabel") }}</span>
                <span class="flex-1 text-body-secondary">{{ (workspace as any).defaultLocale || '—' }}</span>
              </div>
              <div class="flex flex-col gap-3xsmall md:flex-row md:gap-small">
                <span class="flex-1 text-body-secondary content-neutral-medium">{{ t("workspace.currencyLabel") }}</span>
                <span class="flex-1 text-body-secondary">{{ workspace.currency || '—' }}</span>
              </div>
              <div class="flex flex-col gap-3xsmall md:flex-row md:gap-small">
                <span class="flex-1 text-body-secondary content-neutral-medium">{{ t("workspace.timezoneLabel") }}</span>
                <span class="flex-1 text-body-secondary">{{ workspace.timezone || '—' }}</span>
              </div>
            </div>
          </Card>

          <SubscriptionCard
            :subscription="workspace.subscription"
            :can-change="can(AdminPermission.WorkspaceOrdersWrite)"
            @change-plan="openChangePlan"
          />
        </div>
      </template>

      <template #users>
        <MemberList :workspace-i-d="workspaceID" />
      </template>

      <template #orders>
        <OrderList :workspace-i-d="workspaceID" embedded />
      </template>

      <template #workspace-orders>
        <WorkspaceOrderList :workspace-i-d="workspaceID" embedded />
      </template>
    </Tabs>
  </div>

  <WorkspaceFormModal ref="formRef" @saved="onSaved" />
  <ManualSaleModal ref="saleRef" @saved="reload" />
</template>

<style scoped lang="scss">
:deep(.tabs__content) {
  padding: token("padding-medium") 0;
}

:deep(.tabs__content > div) {
  display: flex;
  flex-direction: column;
  gap: token("gap-medium");
}
</style>
